import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';

import {useImageBlock} from './useImageBlock';

import type {Media} from 'types/payload-types';

interface ComponentProps {
    /** Optional caption shown below the image. */
    caption?: string | null;
    /** The populated media document or its ID. */
    image?: Media | string | null;
    /** Test selector for the block. */
    testId?: string;
}

/**
 * Renders a responsive editorial image with optional caption.
 *
 * @param props         The block properties.
 * @param props.caption Optional image caption.
 * @param props.image   The populated media document or its ID.
 * @param props.testId  An optional test ID. Defaults to `ImageBlock`.
 * @returns The image block or null when the media relation is not populated.
 */
const ImageBlock = ({caption, image, testId = 'ImageBlock'}: ComponentProps) => {
    const imageData = useImageBlock(image);

    if (!imageData) return null;

    return (
        <Figure data-cy={testId}>
            <ResponsiveImage
                alt={imageData.alt}
                focalX={imageData.focalX}
                focalY={imageData.focalY}
                sizes="100vw"
                src={imageData.src}
                srcSet={imageData.srcSet}
                testId={`${testId}-image`}
            />
            {caption && <Caption>{caption}</Caption>}
        </Figure>
    );
};

ImageBlock.displayName = 'ImageBlock';

export {ImageBlock};

const Figure = styled.figure`
    margin: 0;
    max-width: 112rem;
    width: 100%;
`;

const Caption = styled.figcaption`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    line-height: 1.5;
    margin-top: ${spacing(2)};
`;